import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import sceneDetectionHeroSvg from '../assets/scene-detection-hero.svg';
import sceneDetectionBenefitsSvg from '../assets/scene-detection-benefits.svg';
import sceneDetectionHeroPng from '../assets/scene-detection-hero.png';
import sceneDetectionBenefitsPng from '../assets/scene-detection-benefits.png';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import SceneDetectionImage from '../components/SceneDetectionImage';
import Footer from '../components/Footer';

const LandingPage: React.FC = () => {
  const features = [
    {
      title: 'Fast Processing',
      description: 'Process your videos quickly with our optimized algorithms',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'High Accuracy',
      description: 'Get precise scene detection results every time',
      icon: <AutoFixHighIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'Easy Export',
      description: 'Export your scenes in multiple formats',
      icon: <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
  ];

  const benefits = [
    {
      title: 'Save Time',
      description: 'Automate the tedious task of manual scene detection',
      icon: <CheckCircleOutlineIcon sx={{ color: 'primary.main' }} />,
    },
    {
      title: 'Professional Quality',
      description: 'Get consistent, high-quality results every time',
      icon: <CheckCircleOutlineIcon sx={{ color: 'primary.main' }} />,
    },
    {
      title: 'Secure Processing',
      description: 'Your videos are processed securely and privately',
      icon: <SecurityIcon sx={{ color: 'primary.main' }} />,
    },
    {
      title: '24/7 Support',
      description: 'Get help whenever you need it',
      icon: <SupportAgentIcon sx={{ color: 'primary.main' }} />,
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Navbar />
      <Box sx={{ flex: 1 }}>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(110, 58, 228, 0.1) 0%, rgba(155, 111, 246, 0.1) 100%)',
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 }, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: 'linear-gradient(45deg, #6E3AE4 30%, #9B6FF6 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Detect Scenes in Your Videos
              </Typography>
              <Typography 
                variant="h5" 
                color="text.secondary" 
                sx={{ 
                  mb: 4,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  lineHeight: 1.6,
                }}
              >
                Automatically identify and extract scenes from your videos with our advanced AI technology. Save time and improve your workflow.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  component={RouterLink}
                  to="/scene-detection"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    background: 'linear-gradient(45deg, #6E3AE4 30%, #9B6FF6 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4C28A8 30%, #6E3AE4 90%)',
                    },
                  }}
                >
                  Get Started
                </Button>
                <Button
                  component={RouterLink}
                  to="/pricing"
                  variant="outlined"
                  size="large"
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'rgba(110, 58, 228, 0.04)',
                    },
                  }}
                >
                  View Pricing
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -20,
                    right: -20,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(45deg, rgba(110, 58, 228, 0.1) 0%, rgba(155, 111, 246, 0.1) 100%)',
                    borderRadius: 4,
                    zIndex: -1,
                  },
                }}
              >
                <SceneDetectionImage
                  svgSrc={sceneDetectionHeroSvg}
                  pngSrc={sceneDetectionHeroPng}
                  alt="Scene Detection"
                  fallbackText="Scene Detection Visualization"
                  rotation={0}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
        <Typography
          variant="h3"
          component="h2"
          align="center"
          sx={{ 
            mb: 2,
            fontWeight: 600,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          Why Choose Our Scene Detector?
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
        >
          Experience the power of AI-driven scene detection with our advanced features
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'all 0.2s',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 3,
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography
                  variant="h5"
                  component="h3"
                  sx={{ mb: 2, fontWeight: 600 }}
                >
                  {feature.title}
                </Typography>
                <Typography color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h2"
            align="center"
            sx={{ 
              mb: 2,
              fontWeight: 600,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            How It Works
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6, maxWidth: '800px', mx: 'auto' }}
          >
            Get started with scene detection in three simple steps
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  1. Upload Your Video
                </Typography>
                <Typography color="text.secondary">
                  Simply upload your video file in any common format
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <AutoFixHighIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  2. AI Processing
                </Typography>
                <Typography color="text.secondary">
                  Our AI analyzes your video to detect scene changes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <VideoLibraryIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                  3. Download Scenes
                </Typography>
                <Typography color="text.secondary">
                  Download individual scenes or the complete package
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 12 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h3"
              component="h2"
              sx={{ 
                mb: 4,
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Benefits of Using Our Scene Detector
            </Typography>
            <Grid container spacing={2}>
              {benefits.map((benefit, index) => (
                <Grid item xs={12} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    {benefit.icon}
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {benefit.title}
                      </Typography>
                      <Typography color="text.secondary">
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  left: -20,
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(45deg, rgba(110, 58, 228, 0.1) 0%, rgba(155, 111, 246, 0.1) 100%)',
                  borderRadius: 4,
                  zIndex: -1,
                },
              }}
            >
              <SceneDetectionImage
                svgSrc={sceneDetectionBenefitsSvg}
                pngSrc={sceneDetectionBenefitsPng}
                alt="Scene Detection Benefits"
                fallbackText="Scene Detection Benefits"
                rotation={0}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 6, md: 12 } }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: 2,
              background: 'linear-gradient(135deg, #6E3AE4 0%, #9B6FF6 100%)',
              color: 'white',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
              },
            }}
          >
            <Typography 
              variant="h3" 
              component="h2" 
              sx={{ 
                mb: 2, 
                fontWeight: 600,
                fontSize: { xs: '2rem', md: '2.5rem' },
                position: 'relative',
              }}
            >
              Ready to Get Started?
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                opacity: 0.9,
                maxWidth: '600px',
                mx: 'auto',
                position: 'relative',
              }}
            >
              Join thousands of users who trust our scene detection technology for their video processing needs.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', position: 'relative' }}>
              <Button
                component={RouterLink}
                to="/scene-detection"
                variant="contained"
                size="large"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                  },
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                Start Detecting Scenes
              </Button>
              <Button
                component={RouterLink}
                to="/pricing"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                  py: 1.5,
                  px: 4,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                }}
              >
                View Pricing
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
      </Box>
      <Footer />
    </Box>
  );
};

export default LandingPage; 